// Offer Add-ons Box Component
// Displays additional focus analysis options as selectable checkboxes

import { ADDITIONAL_FOCUS_ANALYSIS } from "./offers-config.ts";
import type { AdditionalFocusItem } from "./offers-config.ts";

class OfferAddonsBox extends HTMLElement {
  private selectedAddons: Set<number> = new Set();

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  private getSelectedTotal(): number {
    let total = 0;
    this.selectedAddons.forEach((index) => {
      const item = ADDITIONAL_FOCUS_ANALYSIS[index];
      if (item?.price) {
        total += item.price;
      }
    });
    return total;
  }

  private emitPriceChange() {
    const event = new CustomEvent("addons-change", {
      bubbles: true,
      detail: { total: this.getSelectedTotal(), selectedIndices: Array.from(this.selectedAddons) },
    });
    this.dispatchEvent(event);
  }

  private renderAddonItem(item: AdditionalFocusItem, index: number): string {
    const price = item.price ? `€${item.price}` : "";
    const hasDetails = item.description || item.scope;
    const itemId = `addon-item-${index}`;
    const detailsId = `addon-details-${index}`;

    let detailsHtml = "";
    if (hasDetails) {
      let scopeHtml = "";
      if (item.scope && item.scope.length > 0) {
        const scopeItems = item.scope.map(scopeItem =>
          `<li class="text-slate-600 text-xs">
            <span data-en="${scopeItem.en}" data-lv="${scopeItem.lv}" data-it="${scopeItem.it}">
              ${scopeItem.en}
            </span>
          </li>`
        ).join("");
        scopeHtml = `
          <ul class="list-disc ml-5 mt-1 space-y-1">
            ${scopeItems}
          </ul>
        `;
      }

      detailsHtml = `
        <div id="${detailsId}" class="hidden mt-2 pl-6 text-sm">
          ${item.description ? `
            <p class="text-slate-600 text-xs mb-2">
              <span data-en="${item.description.en}" data-lv="${item.description.lv}" data-it="${item.description.it}">
                ${item.description.en}
              </span>
            </p>
          ` : ""}
          ${scopeHtml}
        </div>
      `;
    }

    const expandButton = hasDetails ? `
      <button
        class="ml-2 text-blue-600 hover:text-blue-800 text-xs focus:outline-none addon-toggle"
        data-target="${detailsId}"
        aria-expanded="false"
        aria-label="Toggle details"
      >
        <svg class="w-4 h-4 inline transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>
    ` : "";

    return `
      <div class="addon-item py-2 border-b border-slate-100 last:border-b-0">
        <label class="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" 
            id="${itemId}" 
            data-index="${index}" 
            data-price="${item.price || 0}"
            class="addon-checkbox mt-1 w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500">
          <div class="flex-1">
            <div class="flex items-center justify-between">
              <span class="text-sm text-slate-700" data-en="${item.text.en}" data-lv="${item.text.lv}" data-it="${item.text.it}">
                ${item.text.en}
              </span>
              <div class="flex items-center">
                <span class="text-sm font-semibold text-slate-900">${price}</span>
                ${expandButton}
              </div>
            </div>
          </div>
        </label>
        ${detailsHtml}
      </div>
    `;
  }

  private render() {
    const addonsHtml = ADDITIONAL_FOCUS_ANALYSIS.map((item, index) =>
      this.renderAddonItem(item, index)
    ).join("");

    this.innerHTML = `
      <div class="space-y-0">
        ${addonsHtml}
      </div>
      <div class="mt-4 pt-3 border-t border-slate-200 flex justify-between items-center">
        <span class="text-sm text-slate-600" data-en="Add-ons total:" data-lv="Papildinājumu summa:" data-it="Totale componenti aggiuntivi:">
          Add-ons total:
        </span>
        <span class="addon-total font-bold text-amber-600 text-lg">€0</span>
      </div>
    `;
  }

  private setupEventListeners() {
    // Checkbox change listeners
    this.querySelectorAll(".addon-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        const target = e.target as HTMLInputElement;
        const index = parseInt(target.dataset.index || "0", 10);

        if (target.checked) {
          this.selectedAddons.add(index);
        } else {
          this.selectedAddons.delete(index);
        }

        // Update total display
        const totalElement = this.querySelector(".addon-total");
        if (totalElement) {
          totalElement.textContent = `€${this.getSelectedTotal()}`;
        }

        this.emitPriceChange();
      });
    });

    // Toggle expand/collapse for details
    this.querySelectorAll(".addon-toggle").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const target = (button as HTMLElement).getAttribute("data-target");
        if (target) {
          const detailsElement = this.querySelector(`#${target}`);
          const svgIcon = button.querySelector("svg");

          if (detailsElement) {
            const isHidden = detailsElement.classList.contains("hidden");

            if (isHidden) {
              detailsElement.classList.remove("hidden");
              button.setAttribute("aria-expanded", "true");
              if (svgIcon) {
                svgIcon.style.transform = "rotate(180deg)";
              }
            } else {
              detailsElement.classList.add("hidden");
              button.setAttribute("aria-expanded", "false");
              if (svgIcon) {
                svgIcon.style.transform = "rotate(0deg)";
              }
            }
          }
        }
      });
    });
  }
}

customElements.define("offer-addons-box", OfferAddonsBox);

export { OfferAddonsBox };

